import { Injectable } from '@angular/core';
import { AbcCore, CollectionHelper, CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { ObjectTypeInfoViewModel, TargetColumnValue } from '@barthauer/abc/core/data';
import { ParameterNames } from '../abc-arena.service';
import { ArenaService } from '../core/arena.service';
import { TargetLayoutInfo } from '../core/higon/higon.model';
import { NavigationLayoutViewModel } from '../core/navigation/navigation.viewModel';
import { LayoutViewModel } from '../core/viewModels/layout.viewModel';

@Injectable({
  providedIn: 'root',
})
export class GoToService {
  private matchingLayoutsMapper: Map<string, NavigationLayoutViewModel[]> = new Map();

  constructor(private arena: ArenaService, private core: AbcCore) {}

  private async collectLayoutsOfAllDerivedTypes(
    objectType: ObjectTypeInfoViewModel
  ): Promise<NavigationLayoutViewModel[]> {
    const matchingLayouts: NavigationLayoutViewModel[] = [];
    if (objectType?.allDerivedTypes) {
      for (let ot of objectType.allDerivedTypes) {
        const layouts = await this.findMatchingLayouts(ot.name);
        if (layouts?.length) {
          for (const ly of layouts) {
            matchingLayouts.push(ly);
          }
        }
      }
    }
    return this.distinctLayoutArray(matchingLayouts);
  }

  private distinctLayoutArray(input: NavigationLayoutViewModel[]): NavigationLayoutViewModel[] {
    return CollectionHelper.distinct(
      input?.sort((l1, l2) => CompareHelper.compare(l1.layoutName, l2.layoutName)),
      (_) => _?.layoutCaption
    );
  }

  private async findMatchingLayouts(objectType: string): Promise<NavigationLayoutViewModel[]> {
    return this.findMatchingLayoutsCore(await this.core.objectSpace.getObjectType(objectType).toPromise());
  }

  private findMatchingLayoutsCore(ot: ObjectTypeInfoViewModel): NavigationLayoutViewModel[] {
    let navigationNodesByObjectType = this.arena.navigationViewModelData.findLayoutsByObjectType(ot.name);
    if (!navigationNodesByObjectType?.length && ot.baseType) {
      navigationNodesByObjectType = this.findMatchingLayoutsCore(ot.baseType);
    }
    return navigationNodesByObjectType;
  }

  public async lookForPossibleTargets(
    objectType,
    refObjectId,
    foreignObjectType
  ): Promise<NavigationLayoutViewModel[]> {
    const _matchingLayoutsKey = this.matchingLayoutsKey(objectType, refObjectId, foreignObjectType);
    let matchingLayouts = this.matchingLayoutsMapper.get(_matchingLayoutsKey);
    if (matchingLayouts) {
      return matchingLayouts;
    } else if (refObjectId) {
      matchingLayouts = new Array<NavigationLayoutViewModel>();
      if (objectType) {
        const matchingNaviNodes = await this.findMatchingLayouts(objectType);
        if (matchingNaviNodes) {
          for (let layout of matchingNaviNodes) {
            matchingLayouts.push(layout);
          }
          matchingLayouts = this.distinctLayoutArray(matchingLayouts);
          this.matchingLayoutsMapper.set(_matchingLayoutsKey, matchingLayouts);
          return matchingLayouts;
        }
      } else {
        matchingLayouts = await this.collectLayoutsOfAllDerivedTypes(foreignObjectType);
        this.matchingLayoutsMapper.set(_matchingLayoutsKey, matchingLayouts);
        return matchingLayouts;
      }
    } else {
      matchingLayouts = await this.collectLayoutsOfAllDerivedTypes(foreignObjectType);
      this.matchingLayoutsMapper.set(_matchingLayoutsKey, matchingLayouts);
      return matchingLayouts;
    }
  }

  private matchingLayoutsKey(objectType, refObjectId, foreignObjectType) {
    return refObjectId ? `${objectType}_${refObjectId}_${foreignObjectType}` : `${objectType}_${foreignObjectType}`;
  }

  public async getPossibleTargetLayouts(
    objectType: string | number,
    oPath: string,
    id: string,
    values: TargetColumnValue[],
    currentLayout?: LayoutViewModel,
    openInNewTab?: boolean
  ): Promise<{ layout: NavigationLayoutViewModel; target: TargetLayoutInfo }[]> {
    const targetObjects = ConvertHelper.arrayBufferToObjectArray<any>(
      await this.core.data
        .readTargetObjectsFromPath({
          Object: {
            ObjectKey: { Id: id, ObjectType: objectType },
            TargetColumns: values,
          },
          OPath: oPath,
          IdParameterName: ParameterNames.IdOfObjectInParentLayout,
        })
        .toPromise()
    );
    if (targetObjects) {
      const allMatchingLayouts: { layout: NavigationLayoutViewModel; target: TargetLayoutInfo }[] = [];
      for (const t of targetObjects) {
        const otvm = await this.core.objectSpace.getObjectType(t.TargetObjectType).toPromise();
        const filter: string = t.TargetFilter;
        const column: string = t.Column;
        const isColumnInTarget: boolean = t.IsColumnInTarget;
        let matchingLayouts = this.ignoreCurrentLayout(
          await this.findMatchingLayouts(otvm.name),
          filter,
          currentLayout,
          id
        );
        if (!matchingLayouts?.length) {
          matchingLayouts = this.ignoreCurrentLayout(
            await this.collectLayoutsOfAllDerivedTypes(otvm),
            filter,
            currentLayout,
            id
          );
        }
        const target: TargetLayoutInfo = {
          layout: undefined,
          targetLayoutOPath: filter,
          sourceId: undefined,
          sourceObjectType: undefined,
          column: column,
          isColumnInTarget: isColumnInTarget,
          denyInsert: false,
          openInNewTab: openInNewTab,
        };
        matchingLayouts = this.distinctLayoutArray(matchingLayouts);
        allMatchingLayouts.push(
          ...matchingLayouts.map((_) => {
            return { layout: _, target: target };
          })
        );
        if (!currentLayout && allMatchingLayouts.length) {
          break;
        }
      }
      return allMatchingLayouts.sort((a, b) => {
        const captionA = a.layout.layoutCaption?.toUpperCase();
        const captionB = b.layout.layoutCaption?.toUpperCase();
        if (captionA < captionB) {
          return -1;
        }
        if (captionA > captionB) {
          return 1;
        }
        return 0;
      });
    }

    return [];
  }

  private ignoreCurrentLayout(
    layouts: NavigationLayoutViewModel[],
    targetFilter: string,
    currentLayout?: LayoutViewModel,
    id?: string
  ): NavigationLayoutViewModel[] {
    const isMatchingTargetFilter =
      targetFilter === "Id = '" + id + "'" || targetFilter === 'Id = $' + ParameterNames.IdOfObjectInParentLayout;
    return layouts?.filter((_) => _.layoutName !== currentLayout?.id || !isMatchingTargetFilter);
  }
}
